import React, { useState } from 'react';
import * as formStyles from './../../styles/forms.module.scss';
import { Button } from '../buttons';
import AddressLookupForm from './address-lookup';

const createInitialFormData = (fields) => {
    let initialState = {};

    fields.forEach((field) => {
      field.inputs?.forEach((inputOrInputGroup) => {
          let inputGroup = [];
          let value = '';
          if (Array.isArray(inputOrInputGroup))
              inputGroup = inputOrInputGroup;
          else 
              inputGroup.push(inputOrInputGroup);

          inputGroup.forEach((input) => {
              if (input.defaultValue && input.defaultValue !== '')
                  value = input.defaultValue;
              else if (input.type === 'checkbox')
                  value = false;

              initialState[input.name] = value;
          });
      });
    });

    // Add the _gotcha field to the initial state
    initialState['_gotcha'] = '';

    return initialState;
};

const Form = ({
    fields,
    additionalFields = {},
    endpoint
}) => {
    const [formData, setFormData] = useState(createInitialFormData(fields));

    const [formStatus, setFormStatus] = useState('idle');

    const [invalidFields, setInvalidFields] = useState({});

    const [successMessage, setSuccessMessage] = useState('');

    const handleChange = (event) => {
      let name, value, checked, type  = '';
      if (event?.target) {
        name = event.target.name;
        value = event.target.value;
        checked = event.target.checked;
        type = event.target.type;
      } else {
        name = event.name;
        value = event.value;
        checked = event.checked;
        type = event.type;
      }

      if (type && type === 'checkbox')
        value = checked;
      
      setFormData((prevState) => ({
          ...prevState,
          [name]: value
      }));
    };

    const validateForm = () => {
        let requiredFields = [];
        // Extract required fields from the fields prop
        fields.forEach((field) => {
            if (field.legend) {
                field.inputs.forEach((inputOrInputGroup) => {
                    if (Array.isArray(inputOrInputGroup)) {
                        inputOrInputGroup.forEach((input) => {
                            if (input.required) {
                                requiredFields.push(input.name);
                            }
                        });
                    } else {
                        const input = inputOrInputGroup;
                        if (input.required) {
                            requiredFields.push(input.name);
                        }
                    }
                });
            } else if (field.type === 'consent') {
                field.inputs.forEach((input) => {
                    if (input.required) {
                        requiredFields.push(input.name);
                    }
                });
            }
        });
        
        let isValid = true;
        let updatedInvalidFields = {};

        for (const field of requiredFields) {
            if (!formData[field]) {
                isValid = false;
                updatedInvalidFields[field] = true;
            } else {
                updatedInvalidFields[field] = false;
            }
        }

        setInvalidFields(updatedInvalidFields);
        return isValid;
    };

    const handleSubmit = async (event) => {
      event.preventDefault();
      setFormStatus('disabled');
      if (!validateForm()) {
        alert('Please fill in all required fields and accept the terms and conditions.');
        setFormStatus('idle');
        return;
      }

      if (typeof window !== 'undefined')
        additionalFields.url = window.location.href;

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({...additionalFields,...formData})
        });

        if (response.ok) {
          setSuccessMessage('Your message has been sent successfully. Thank you!');
          setFormStatus('idle');
          setFormData(createInitialFormData(fields));

          //Find the closest div with [class*='modalCardInner'] and scroll it down to the success message
          let modalCardInner = event.target.closest("div[class*='modalCardInner']");
          if (modalCardInner) {
            setTimeout(() => modalCardInner.scrollTop = modalCardInner.scrollHeight, 100);
          }

          if(typeof window.dataLayer != 'undefined') {
            window.dataLayer.push({'event': 'form_submit', 'status': 'success', 'endpoint': endpoint, 'formData': formData});
          }
        } else {
          throw new Error('Failed to submit the form.');
        }
      } catch (error) {
        setFormStatus('idle');
        alert('Error submitting the form: ' + error.message);
      }
    };
    
    return (
        <form className={formStyles.form} onSubmit={handleSubmit} disabled={formStatus === 'disabled'}>
            <input
                type="hidden"
                name="_gotcha"
                value={formData["_gotcha"]}
                onChange={handleChange}
                style={{ display: 'none !important' }}
            />
            {fields.map((field, index) => {
                if (field.legend) {
                    return (
                      <fieldset key={index}>
                        <legend>{field.legend}</legend>
                        {field.inputs.map((inputOrInputGroup, i) => {
                          if (Array.isArray(inputOrInputGroup)) {
                            return (
                              <div key={i} className={formStyles.splitRow}>
                                {inputOrInputGroup.map((input, j) => (
                                  <Input
                                    key={j}
                                    input={input}
                                    handleChange={handleChange}
                                    formData={formData}
                                    invalidFields={invalidFields}
                                  />
                                ))}
                              </div>
                            );
                          } else {
                            const input = inputOrInputGroup;
                            return (
                              <Input
                                key={i}
                                input={input}
                                handleChange={handleChange}
                                formData={formData}
                                invalidFields={invalidFields}
                              />
                            );
                          }
                        })}
                      </fieldset>
                    );
                } else if (field.inputs) {
                    return field.inputs.map((input, i) => (
                      <Input
                        key={i}
                        input={input}
                        handleChange={handleChange}
                        formData={formData}
                        invalidFields={invalidFields}
                      />
                    ));
                } else if (field.type === 'text') {
                    return field.content && <p>{field.content}</p>
                } else if (field.type === 'button') {
                    return <Button key={index} elementType="button" content={field.content} colorData={field?.color} />;
                } else {
                    return null;
                }
            })}
            {
                successMessage && (
                    <div className={formStyles.successMessage}>
                        <h2>Success!</h2>
                        {successMessage}
                    </div>
                )
            }
        </form>
    );
}

const Input = ({
    input,
    handleChange,
    formData,
    invalidFields,
  }) => {
    if (input.required === true && typeof input.label === 'string' && input.label.slice(-1) !== "*" )
        input.label += "*";

    if (input.type === 'address-lookup') {
        return (
          <AddressLookupForm
            fields={input.subFields}
            formData={formData}
            handleChange={handleChange}
            invalidFields={invalidFields}
          />    
        )
    } else if (input.type === 'checkbox') {
        return (
          <label 
          style={invalidFields[input.name] ? { border: '1px solid red' } : {}}>
            <input
              name={input.name}
              type={input.type}
              checked={formData[input.name]}
              onChange={handleChange}
            />
            {input.label}
          </label>
        );
    } else if (input.type === 'hidden') {
        <input
              name={input.name}
              type="checkbox"
              checked={formData[input.name]}
              onChange={handleChange}
            />
    } else if (input.type === 'select') {
      return (
        <label>
          {input.label}
          <select
            name={input.name}
            value={formData[input.name]}
            onChange={handleChange}
            style={invalidFields[input.name] ? { border: '1px solid red' } : {}}
            defaultValue={input.defaultValue}
          >
            {input.values.map((option, o) => (
              <option key={o} value={option.value} disabled={option.disabled}>
                {option.text}
              </option>
            ))}
          </select>
        </label>
      );
    } else if (input.type === 'textarea') {
      return (
        <label>
          {input.label}
          <textarea
            name={input.name}
            value={formData[input.name]}
            placeholder={input.placeholder}
            onChange={handleChange}
            style={invalidFields[input.name] ? { border: '1px solid red' } : {}}
          ></textarea>
        </label>
      );
    } else {
      return (
        <label>
          {input.label}
          <input
            type={input.type}
            name={input.name}
            value={formData[input.name]}
            placeholder={input.placeholder}
            pattern={input.pattern}
            onChange={handleChange}
            style={invalidFields[input.name] ? { border: '1px solid red' } : {}}
          />
        </label>
      );
    }
  };
  

export default Form;
